import React, { useEffect, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { getAllOrders, ORDER_STATUS_LABELS } from "../../firebase/services/orders";
import { getAllProducts } from "../../firebase/services/products";
import { getAllUsers }    from "../../firebase/services/users";

// =========================
// Module-level components
// =========================

const BarChart = ({ data, color = "bg-blue-500" }) => {
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <div className="flex items-end gap-1 h-12">
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center justify-end gap-0.5 group relative h-full">
          <div
            className={`w-full rounded-sm ${color} transition-all duration-500 opacity-80 group-hover:opacity-100`}
            style={{ height: `${(d.value / max) * 100}%`, minHeight: d.value > 0 ? 4 : 1 }}
          />
          <span className="absolute -top-7 left-1/2 -translate-x-1/2 bg-gray-700 text-white text-xs px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
            {d.label}: {d.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const iconBgMap = {
  "text-blue-400":   "bg-blue-600/20",
  "text-green-400":  "bg-green-600/20",
  "text-orange-400": "bg-orange-600/20",
  "text-purple-400": "bg-purple-600/20",
};

const StatCard = ({ icon, label, value, sub, color, onClick, chart, chartColor }) => (
  <div
    onClick={onClick}
    className={`bg-gray-800 rounded-xl p-5 border border-gray-700/50 flex flex-col gap-3 ${
      onClick ? "cursor-pointer hover:border-blue-500/50 transition-colors duration-200" : ""
    }`}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">{label}</p>
        <p className={`text-3xl font-bold ${color}`}>{value}</p>
        {sub && <p className="text-gray-500 text-xs mt-1">{sub}</p>}
      </div>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconBgMap[color]}`}>
        <i className={`bi ${icon} text-xl ${color}`} />
      </div>
    </div>
    {chart && <BarChart data={chart} color={chartColor} />}
  </div>
);

const STATUS_COLORS = {
  to_pay:        "bg-yellow-600",
  to_ship:       "bg-blue-600",
  to_receive:    "bg-purple-600",
  completed:     "bg-green-600",
  cancelled:     "bg-red-600",
  return_refund: "bg-orange-600",
};

const StatusPill = ({ status }) => (
  <span className={`inline-block px-2 py-0.5 rounded text-xs text-white ${STATUS_COLORS[status] ?? "bg-gray-600"}`}>
    {ORDER_STATUS_LABELS[status] ?? status}
  </span>
);

const SectionHeader = ({ icon, title, action }) => (
  <div className="flex items-center justify-between mb-3">
    <h2 className="text-sm font-bold text-gray-300 uppercase tracking-wider flex items-center gap-2">
      <i className={`bi ${icon} text-blue-400`} />
      {title}
    </h2>
    {action}
  </div>
);

const formatDate = (d) => d ? new Date(d).toLocaleDateString() : "N/A";

// =========================
// Main Component
// =========================

function Dashboard() {
  const { setPageTitle, setHeaderAction } = useOutletContext();
  const navigate = useNavigate();

  const [orders,   setOrders]   = useState([]);
  const [products, setProducts] = useState([]);
  const [users,    setUsers]    = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    setPageTitle("Dashboard");
    setHeaderAction(null);
    return () => { setPageTitle("Admin"); setHeaderAction(null); };
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [allOrders, allProducts, allUsers] = await Promise.all([
          getAllOrders({ force: true }),
          getAllProducts({ force: true }),
          getAllUsers(),
        ]);
        setOrders(allOrders);
        setProducts(allProducts);
        setUsers(allUsers);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  /* ─── Derived stats ─── */
  const completedOrders = orders.filter((o) => o.status === "completed");
  const totalRevenue    = completedOrders.reduce((s, o) => s + (o.totalAmount ?? 0), 0);

  const statusCounts = {
    to_pay:        orders.filter((o) => o.status === "to_pay").length,
    to_ship:       orders.filter((o) => o.status === "to_ship").length,
    to_receive:    orders.filter((o) => o.status === "to_receive").length,
    completed:     orders.filter((o) => o.status === "completed").length,
    cancelled:     orders.filter((o) => o.status === "cancelled").length,
    return_refund: orders.filter((o) => o.status === "return_refund").length,
  };

  const lowStock    = products.filter((p) => p.stock <= 5);
  const recentOrders = [...orders]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 6);

  const getUser = (id) => users.find((u) => u.id === id);
  const getFullName = (id) => {
    const u = getUser(id);
    if (!u) return "Unknown";
    const parts = [u.firstName, u.middleName, u.lastName].filter(Boolean);
    return parts.length ? parts.join(" ") : u.username;
  };

  /* ─── Last 7 days order chart ─── */
  const last7 = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    const label   = d.toLocaleDateString("en-US", { weekday: "short" });
    const dateStr = d.toISOString().slice(0, 10);
    const value   = orders.filter((o) => o.createdAt?.slice(0, 10) === dateStr).length;
    return { label, value };
  });

  /* ─── Revenue by category chart ─── */
  const categoryRevenue = {};
  completedOrders.forEach((o) => {
    (o.items ?? []).forEach((item) => {
      const p   = products.find((p) => p.id === item.productId);
      const cat = p?.category ?? "Other";
      categoryRevenue[cat] = (categoryRevenue[cat] ?? 0) + item.price * item.quantity;
    });
  });
  const categoryChart = Object.entries(categoryRevenue)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 7)
    .map(([label, value]) => ({ label, value: Math.round(value * 100) / 100 }));

  /* ─── Stock distribution chart ─── */
  const stockChart = [...products]
    .sort((a, b) => a.stock - b.stock)
    .slice(0, 7)
    .map((p) => ({ label: p.name, value: p.stock }));

  /* ─── Order status ratio ─── */
  const statusRatioKeys = ["to_pay", "to_ship", "to_receive", "completed", "cancelled", "return_refund"];
  const statusRatioColors = {
    to_pay:        "bg-yellow-500",
    to_ship:       "bg-blue-500",
    to_receive:    "bg-purple-500",
    completed:     "bg-green-500",
    cancelled:     "bg-red-500",
    return_refund: "bg-orange-500",
  };
  const orderStatusRatio = orders.length
    ? statusRatioKeys
        .filter((k) => statusCounts[k] > 0)
        .map((k) => ({
          label: ORDER_STATUS_LABELS[k],
          value: statusCounts[k],
          color: statusRatioColors[k],
          pct:   Math.round((statusCounts[k] / orders.length) * 100),
        }))
    : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        <svg className="animate-spin h-8 w-8 mr-3 text-blue-500" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        Loading dashboard…
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">

      {/* ─── Stat Cards ─── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon="bi-receipt"         label="Total Orders"
          value={orders.length}     sub={`${statusCounts.to_pay} to pay · ${statusCounts.to_ship} to ship`}
          color="text-blue-400"     chart={last7}  chartColor="bg-blue-500"
          onClick={() => navigate("/admin/orders/all")}
        />
        <StatCard
          icon="bi-currency-dollar" label="Revenue"
          value={`$${totalRevenue.toFixed(2)}`}
          sub={`${statusCounts.completed} completed orders`}
          color="text-green-400"    chart={categoryChart.length ? categoryChart : undefined} chartColor="bg-green-500"
          onClick={() => navigate("/admin/sales")}
        />
        <StatCard
          icon="bi-box-seam"        label="Products"
          value={products.length}   sub={`${lowStock.length} low stock`}
          color="text-orange-400"   chart={stockChart} chartColor="bg-orange-500"
          onClick={() => navigate("/admin/products")}
        />
        <StatCard
          icon="bi-people"          label="Users"
          value={users.length}      sub={`${users.filter((u) => u.role === "admin").length} admins`}
          color="text-purple-400"
          onClick={() => navigate("/admin/users/all")}
        />
      </div>

      {/* ─── Order status + Recent orders ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Order status breakdown */}
        <div className="bg-gray-800 rounded-xl p-5 border border-gray-700/50">
          <SectionHeader icon="bi-pie-chart" title="Order Status" />
          <div className="space-y-3">
            {orderStatusRatio.length === 0 && (
              <p className="text-gray-500 text-sm italic">No orders yet.</p>
            )}
            {orderStatusRatio.map(({ label, value, color, pct }) => (
              <div key={label}>
                <div className="flex justify-between text-xs text-gray-400 mb-1">
                  <span>{label}</span>
                  <span>{value} ({pct}%)</span>
                </div>
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div className={`h-full ${color} rounded-full transition-all duration-700`}
                    style={{ width: `${pct}%` }} />
                </div>
              </div>
            ))}
          </div>

          {/* Status counts grid */}
          <div className="mt-5 pt-4 border-t border-gray-700 grid grid-cols-3 gap-2 text-center">
            {[
              { label: "To Pay",    value: statusCounts.to_pay,     color: "text-yellow-400" },
              { label: "To Ship",   value: statusCounts.to_ship,    color: "text-blue-400" },
              { label: "Completed", value: statusCounts.completed,  color: "text-green-400" },
            ].map(({ label, value, color }) => (
              <div key={label}>
                <p className={`text-xl font-bold ${color}`}>{value}</p>
                <p className="text-gray-500 text-xs">{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Recent orders */}
        <div className="lg:col-span-2 bg-gray-800 rounded-xl p-5 border border-gray-700/50">
          <SectionHeader
            icon="bi-clock-history"
            title="Recent Orders"
            action={
              <button onClick={() => navigate("/admin/orders/all")}
                className="text-blue-400 hover:text-blue-300 text-xs transition-colors">
                View all →
              </button>
            }
          />
          {recentOrders.length === 0 ? (
            <p className="text-gray-500 text-sm italic">No orders yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-gray-500 text-xs uppercase border-b border-gray-700">
                    <th className="text-left pb-2 pr-3">ID</th>
                    <th className="text-left pb-2 pr-3">Customer</th>
                    <th className="text-left pb-2 pr-3">Total</th>
                    <th className="text-left pb-2 pr-3">Status</th>
                    <th className="text-left pb-2">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700/50">
                  {recentOrders.map((o) => (
                    <tr key={o.id}
                      onClick={() => navigate("/admin/orders/all")}
                      className="hover:bg-gray-700/30 transition-colors cursor-pointer">
                      <td className="py-2 pr-3 text-gray-400 font-mono text-xs truncate max-w-[80px]" title={o.id}>
                        #{o.id}
                      </td>
                      <td className="py-2 pr-3 text-white font-medium">{getFullName(o.userId)}</td>
                      <td className="py-2 pr-3 text-green-400 font-semibold">${o.totalAmount?.toFixed(2) ?? "0.00"}</td>
                      <td className="py-2 pr-3"><StatusPill status={o.status} /></td>
                      <td className="py-2 text-gray-400 text-xs">{formatDate(o.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ─── Low stock + Top products ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Low stock alert */}
        <div className="bg-gray-800 rounded-xl p-5 border border-gray-700/50">
          <SectionHeader
            icon="bi-exclamation-triangle"
            title="Low Stock Alert"
            action={
              <button onClick={() => navigate("/admin/products")}
                className="text-blue-400 hover:text-blue-300 text-xs transition-colors">
                Manage →
              </button>
            }
          />
          {lowStock.length === 0 ? (
            <div className="flex items-center gap-2 text-green-400 text-sm">
              <i className="bi bi-check-circle-fill" />
              All products are sufficiently stocked.
            </div>
          ) : (
            <div className="space-y-2">
              {lowStock.map((p) => (
                <div key={p.id}
                  onClick={() => navigate("/admin/products")}
                  className="flex items-center justify-between bg-gray-700/40 rounded-lg px-3 py-2 cursor-pointer hover:bg-gray-700/70 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {p.image ? (
                      <img src={p.image} alt={p.name} className="w-8 h-8 rounded object-cover" />
                    ) : (
                      <div className="w-8 h-8 bg-gray-600 rounded flex items-center justify-center">
                        <i className="bi bi-box text-gray-400 text-xs" />
                      </div>
                    )}
                    <div>
                      <p className="text-white text-sm font-medium">{p.name}</p>
                      <p className="text-gray-500 text-xs">{p.category}</p>
                    </div>
                  </div>
                  <span className={`text-sm text-end font-bold ${p.stock === 0 ? "text-red-400" : "text-orange-400"}`}>
                    {p.stock === 0 ? "Out of stock" : `${p.stock} left`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top products by revenue */}
        <div className="bg-gray-800 rounded-xl p-5 border border-gray-700/50">
          <SectionHeader icon="bi-trophy" title="Top Products by Revenue" />
          {completedOrders.length === 0 ? (
            <p className="text-gray-500 text-sm italic">No completed orders yet.</p>
          ) : (() => {
            const prodRevenue = {};
            completedOrders.forEach((o) => {
              (o.items ?? []).forEach((item) => {
                prodRevenue[item.productId] = (prodRevenue[item.productId] ?? 0) + item.price * item.quantity;
              });
            });
            const topProds = Object.entries(prodRevenue)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 5)
              .map(([id, rev]) => {
                const p = products.find((p) => p.id === id);
                return { id, name: p?.name ?? `Product #${id}`, revenue: rev, category: p?.category ?? "", image: p?.image };
              });
            const maxRev = Math.max(...topProds.map((p) => p.revenue));
            return (
              <div className="space-y-3">
                {topProds.map((p, i) => (
                  <div key={i}
                    onClick={() => navigate(`/admin/products`)}
                    className="cursor-pointer hover:bg-gray-700/30 rounded-lg px-2 py-1 -mx-2 transition-colors"
                  >
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-white font-medium truncate max-w-[60%]">{p.name}</span>
                      <span className="text-green-400 font-semibold">${p.revenue.toFixed(2)}</span>
                    </div>
                    <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full transition-all duration-700"
                        style={{ width: `${(p.revenue / maxRev) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </div>

      {/* ─── User overview ─── */}
      <div className="bg-gray-800 rounded-xl p-5 border border-gray-700/50">
        <SectionHeader
          icon="bi-people"
          title="User Overview"
          action={
            <button onClick={() => navigate("/admin/users/all")}
              className="text-blue-400 hover:text-blue-300 text-xs transition-colors">
              Manage →
            </button>
          }
        />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Total Users",   value: users.length,                                   icon: "bi-people-fill",  color: "text-blue-400" },
            { label: "Admins",        value: users.filter((u) => u.role === "admin").length,  icon: "bi-shield-fill",  color: "text-purple-400" },
            { label: "Regular Users", value: users.filter((u) => u.role === "user").length,   icon: "bi-person-fill",  color: "text-cyan-400" },
            { label: "Active Carts",  value: users.filter((u) => u.cart?.length > 0).length, icon: "bi-cart-fill",    color: "text-orange-400" },
          ].map(({ label, value, icon, color }) => (
            <div key={label} className="bg-gray-700/40 rounded-lg p-4 text-center">
              <i className={`bi ${icon} text-2xl ${color} mb-1 block`} />
              <p className={`text-2xl font-bold ${color}`}>{value}</p>
              <p className="text-gray-400 text-xs mt-1">{label}</p>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}

export default Dashboard;